import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Save } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export const CodeDialog = ({ open, onOpenChange, getHtml, onSave }) => {
  const [code, setCode] = useState("");

  useEffect(() => {
    if (open) setCode(getHtml());
  }, [open, getHtml]);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("HTML copied to clipboard");
    } catch {
      toast.error("Clipboard blocked by browser — select the code and copy manually");
    }
  };

  const onSaveClick = () => {
    if (!code.trim()) {
      toast.error("Code is empty");
      return;
    }
    onSave(code);
    toast.success("HTML saved — preview updated");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="code-dialog" className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-normal">
            HTML <em className="text-accent">code</em>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Copy the code, or paste/edit your own HTML and save it to update the preview. Send, Bulk and Download use the saved version.
          </DialogDescription>
        </DialogHeader>
        <textarea
          data-testid="code-textarea"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          spellCheck={false}
          className="w-full h-[420px] rounded-lg border border-border bg-secondary/40 px-3 py-2.5 font-mono text-[11px] leading-relaxed outline-none focus:ring-2 focus:ring-accent/50 resize-none"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            data-testid="code-copy-btn"
            onClick={onCopy}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-semibold hover:bg-secondary transition-colors"
          >
            <Copy size={13} /> Copy code
          </button>
          <button
            data-testid="code-save-btn"
            onClick={onSaveClick}
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-xs font-semibold hover:opacity-90 transition-opacity"
          >
            <Save size={13} /> Save to preview
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
